import { useMemo } from 'react';

import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';

export interface MonthSelectProps {
  /** Rótulo exibido acima do seletor. */
  label: string;
  /** Valor no formato "YYYY-MM", ou "" para "Todos os meses". */
  value: string;
  onChange: (value: string) => void;
  /** Quantos meses anteriores ao atual exibir (padrão 12). */
  monthsBack?: number;
  width?: string;
  ariaLabel?: string;
}

function formatarMesLongo(data: Date): string {
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(data);
}

/**
 * Seletor de mês de referência: lista discreta (mês atual + N anteriores), não
 * um `<input type="month">` nativo — para filtro de listagem onde o mês é
 * sempre um dos últimos N (Multas, Cobranças), fiel ao protótipo
 * (`MonthSelect.tsx`). Para um campo de formulário onde qualquer mês é válido,
 * ou para período aberto, usar `MonthFilter`/`DateRangeFilter`.
 */
export function MonthSelect({ label, value, onChange, monthsBack = 12, width, ariaLabel }: MonthSelectProps) {
  const opcoes = useMemo(() => {
    const hoje = new Date();
    return Array.from({ length: monthsBack + 1 }, (_, i) => {
      const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
      const ano = data.getFullYear();
      const mes = String(data.getMonth() + 1).padStart(2, '0');
      return { value: `${ano}-${mes}`, label: formatarMesLongo(data) };
    });
  }, [monthsBack]);

  const valorSelecionado = value || 'TODOS';

  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Select value={valorSelecionado} onValueChange={(v) => onChange(v === 'TODOS' ? '' : v)}>
        <SelectTrigger className={width ?? 'w-full sm:w-[200px]'} aria-label={ariaLabel ?? label}>
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="TODOS">Todos os meses</SelectItem>
          {opcoes.map((opcao) => (
            <SelectItem key={opcao.value} value={opcao.value} className="capitalize">
              {opcao.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default MonthSelect;
